import React, { useState } from 'react';
import {
  ArrowLeft, Send, Loader2, FileText,
  Sparkles, MapPin, DollarSign, Clock, CheckCircle
} from 'lucide-react';
import axios from 'axios';
import './CandidateSubmissionForm.css';

const emptyState = {
  candidateName: '', contactNumber: '', candidateEmail: '',
  skillSet: '', clientName: '', currentCompanyName: '',
  totalExperience: '', relevantExperience: '', currentCTC: '',
  expectedCTC: '', noticePeriod: '', lastWorkingDay: '',
  reasonForChange: '', currentlyWorking: 'Yes',
  reasonNotWorking: '', currentLocation: '',
  preferredLocation: '', education: '',
};

export default function CandidateSubmissionForm({ job, vendor, onBack }) {
  const [formData, setFormData]     = useState(emptyState);
  const [resumeFile, setResumeFile] = useState(null);
  const [isLoading, setIsLoading]   = useState(false);
  const [isParsing, setIsParsing]   = useState(false);
  const [autoFilled, setAutoFilled] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  /* ── PDF-only handler ── */
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const isPdf = file.type === 'application/pdf' ||
                  file.name.toLowerCase().endsWith('.pdf');
    if (!isPdf) {
      alert('Only PDF files are accepted.');
      e.target.value = '';
      return;
    }

    setResumeFile(file);
    setAutoFilled(false);
    setIsParsing(true);

    const fd = new FormData();
    fd.append('resume', file);

    try {
      const { data: p } = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/candidates/parse-resume`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setFormData(prev => ({
        ...prev,
        candidateName:      p.candidateName      || prev.candidateName,
        candidateEmail:     p.candidateEmail      || prev.candidateEmail,
        contactNumber:      p.contactNumber       || prev.contactNumber,
        skillSet:           p.skillSet            || prev.skillSet,
        totalExperience:    p.totalExperience     || prev.totalExperience,
        relevantExperience: p.relevantExperience  || prev.relevantExperience,
        currentCompanyName: p.currentCompanyName  || prev.currentCompanyName,
        currentLocation:    p.currentLocation     || prev.currentLocation,
        education:          p.education           || prev.education,
      }));
      setAutoFilled(true);
    } catch (err) {
      alert(
        err.response?.status === 422
          ? 'Could not read this PDF. Please try a different file.'
          : 'Failed to extract data from resume.'
      );
    } finally {
      setIsParsing(false);
    }
  };

  /* ── Submit ── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!vendor?._id) { alert('Vendor profile incomplete. Please re-login.'); return; }
    if (!resumeFile)  { alert('Please upload a PDF resume.'); return; }

    setIsLoading(true);
    const data = new FormData();
    data.append('resume', resumeFile);
    data.append('data', JSON.stringify({
      jobId:         job._id,
      vendorId:      vendor._id,
      candidateData: formData,
      resumeFile:    { name: resumeFile.name, size: resumeFile.size },
    }));

    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/candidates`, data);
      alert(`✅ ${formData.candidateName} submitted successfully!`);
      onBack();
    } catch (err) {
      if (err.response?.status === 409) {
        alert(`🛑 Blocked: ${err.response.data.message}`);
      } else if (err.response?.data?.message) {
        alert(`Failed: ${err.response.data.message}`);
      } else {
        alert('Submission failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fmt = (n) => {
    if (n < 1024) return `${n} B`;
    if (n < 1048576) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1048576).toFixed(1)} MB`;
  };

  /* Checklist for right panel */
  const steps = [
    { label: 'Upload PDF Resume',      done: !!resumeFile },
    { label: 'Candidate Details',      done: !!(formData.candidateName && formData.candidateEmail) },
    { label: 'Employment Status',      done: !!(formData.currentlyWorking) },
    { label: 'Compensation & Client',  done: !!(formData.currentCTC || formData.expectedCTC) },
  ];

  /* ════════════════════════════════ */
  return (
    <div className="csf-wrapper">

      {/* ── TOP BAR ── */}
      <div className="csf-topbar">
        <button className="csf-back-btn" onClick={onBack} disabled={isLoading}>
          <ArrowLeft size={14} /> Back
        </button>

        <div className="csf-topbar-title">
          <h3>Submit Candidate — {job?.role}</h3>
          <p>{job?.companyName} · {job?.location}</p>
        </div>

        {autoFilled && (
          <span className="csf-ai-badge">
            <Sparkles size={10} /> AI Auto-filled
          </span>
        )}
      </div>

      {/* ── BODY ── */}
      <div className="csf-body">

        {/* ════════ LEFT: Scrollable Form ════════ */}
        <div className="csf-left">
          <form id="csf-main-form" onSubmit={handleSubmit}>

            {/* Section 1 – Candidate Details */}
            <div className="csf-section">
              <div className="csf-section-header">
                <div className="csf-section-number">1</div>
                <h4>Candidate Details</h4>
              </div>
              <div className="csf-section-body">

                <div className="csf-grid-3">
                  <div className="csf-group">
                    <label className="csf-label">Full Name <span className="csf-required">*</span></label>
                    <input className={`csf-input ${autoFilled && formData.candidateName ? 'auto-filled' : ''}`}
                      type="text" name="candidateName" value={formData.candidateName}
                      onChange={handleChange} placeholder="e.g. John Doe" required />
                  </div>
                  <div className="csf-group">
                    <label className="csf-label">Email <span className="csf-required">*</span></label>
                    <input className={`csf-input ${autoFilled && formData.candidateEmail ? 'auto-filled' : ''}`}
                      type="email" name="candidateEmail" value={formData.candidateEmail}
                      onChange={handleChange} placeholder="john@example.com" required />
                  </div>
                  <div className="csf-group">
                    <label className="csf-label">Contact Number</label>
                    <input className={`csf-input ${autoFilled && formData.contactNumber ? 'auto-filled' : ''}`}
                      type="tel" name="contactNumber" value={formData.contactNumber}
                      onChange={handleChange} placeholder="+91 9876543210" />
                  </div>
                </div>

                <div className="csf-grid-3">
                  <div className="csf-group">
                    <label className="csf-label">Total Experience</label>
                    <input className={`csf-input ${autoFilled && formData.totalExperience ? 'auto-filled' : ''}`}
                      type="text" name="totalExperience" value={formData.totalExperience}
                      onChange={handleChange} placeholder="e.g. 5 Years" />
                  </div>
                  <div className="csf-group">
                    <label className="csf-label">Relevant Experience</label>
                    <input className={`csf-input ${autoFilled && formData.relevantExperience ? 'auto-filled' : ''}`}
                      type="text" name="relevantExperience" value={formData.relevantExperience}
                      onChange={handleChange} placeholder="e.g. 3 Years in React" />
                  </div>
                  <div className="csf-group">
                    <label className="csf-label">Current Company</label>
                    <input className={`csf-input ${autoFilled && formData.currentCompanyName ? 'auto-filled' : ''}`}
                      type="text" name="currentCompanyName" value={formData.currentCompanyName}
                      onChange={handleChange} placeholder="Current Organization" />
                  </div>
                </div>

                <div className="csf-group">
                  <label className="csf-label">Skill Set</label>
                  <textarea className={`csf-textarea ${autoFilled && formData.skillSet ? 'auto-filled' : ''}`}
                    name="skillSet" rows={3} value={formData.skillSet}
                    onChange={handleChange} placeholder="Java, React, Node.js, AWS, Python…" />
                </div>

                <div className="csf-grid-2">
                  <div className="csf-group">
                    <label className="csf-label">Current Location</label>
                    <input className={`csf-input ${autoFilled && formData.currentLocation ? 'auto-filled' : ''}`}
                      type="text" name="currentLocation" value={formData.currentLocation}
                      onChange={handleChange} placeholder="e.g. Bangalore" />
                  </div>
                  <div className="csf-group">
                    <label className="csf-label">Preferred Location</label>
                    <input className="csf-input" type="text" name="preferredLocation"
                      value={formData.preferredLocation} onChange={handleChange}
                      placeholder="e.g. Hyderabad, Remote" />
                  </div>
                </div>

                <div className="csf-group">
                  <label className="csf-label">Education</label>
                  <input className={`csf-input ${autoFilled && formData.education ? 'auto-filled' : ''}`}
                    type="text" name="education" value={formData.education}
                    onChange={handleChange} placeholder="e.g. B.Tech Computer Science" />
                </div>

              </div>
            </div>

            {/* Section 2 – Employment Status */}
            <div className="csf-section">
              <div className="csf-section-header">
                <div className="csf-section-number">2</div>
                <h4>Employment Status</h4>
              </div>
              <div className="csf-section-body">

                <div className="csf-grid-2">
                  <div className="csf-group">
                    <label className="csf-label">Currently Working?</label>
                    <select className="csf-select" name="currentlyWorking"
                      value={formData.currentlyWorking} onChange={handleChange}>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </div>
                  {formData.currentlyWorking === 'Yes' && (
                    <div className="csf-group">
                      <label className="csf-label">Notice Period</label>
                      <input className="csf-input" type="text" name="noticePeriod"
                        value={formData.noticePeriod} onChange={handleChange}
                        placeholder="e.g. 30 Days" />
                    </div>
                  )}
                </div>

                {formData.currentlyWorking === 'Yes' && (
                  <div className="csf-grid-2">
                    <div className="csf-group">
                      <label className="csf-label">Last Working Day</label>
                      <input className="csf-input" type="date" name="lastWorkingDay"
                        value={formData.lastWorkingDay} onChange={handleChange} />
                    </div>
                    <div className="csf-group">
                      <label className="csf-label">Reason for Change</label>
                      <input className="csf-input" type="text" name="reasonForChange"
                        value={formData.reasonForChange} onChange={handleChange}
                        placeholder="e.g. Better Opportunity" />
                    </div>
                  </div>
                )}

                {formData.currentlyWorking === 'No' && (
                  <div className="csf-group">
                    <label className="csf-label">Reason Not Working</label>
                    <input className="csf-input" type="text" name="reasonNotWorking"
                      value={formData.reasonNotWorking} onChange={handleChange}
                      placeholder="Reason for employment gap" />
                  </div>
                )}

              </div>
            </div>

            {/* Section 3 – Compensation */}
            <div className="csf-section">
              <div className="csf-section-header">
                <div className="csf-section-number">3</div>
                <h4>Compensation &amp; Client</h4>
              </div>
              <div className="csf-section-body">
                <div className="csf-grid-3">
                  <div className="csf-group">
                    <label className="csf-label">Current CTC</label>
                    <input className="csf-input" type="text" name="currentCTC"
                      value={formData.currentCTC} onChange={handleChange}
                      placeholder="e.g. 10 LPA" />
                  </div>
                  <div className="csf-group">
                    <label className="csf-label">Expected CTC</label>
                    <input className="csf-input" type="text" name="expectedCTC"
                      value={formData.expectedCTC} onChange={handleChange}
                      placeholder="e.g. 15 LPA" />
                  </div>
                  <div className="csf-group">
                    <label className="csf-label">Client Name</label>
                    <input className="csf-input" type="text" name="clientName"
                      value={formData.clientName} onChange={handleChange}
                      placeholder="End client name" />
                  </div>
                </div>
              </div>
            </div>

          </form>
        </div>

        {/* ════════ RIGHT: Sticky Panel ════════ */}
        <div className="csf-right">

          {/* Job Info */}
          <div className="csf-job-card">
            <div className="csf-job-card-title">Applying For</div>
            <div className="csf-job-card-role">{job?.role}</div>
            <div className="csf-job-card-company">{job?.companyName}</div>
            <div className="csf-job-card-meta">
              {job?.location && (
                <div className="csf-job-card-meta-row">
                  <MapPin size={12} /> {job.location}
                </div>
              )}
              {job?.ctc && (
                <div className="csf-job-card-meta-row">
                  <DollarSign size={12} /> {job.ctc}
                </div>
              )}
              {job?.applyByDate && (
                <div className="csf-job-card-meta-row">
                  <Clock size={12} /> Apply by {job.applyByDate}
                </div>
              )}
            </div>
          </div>

          {/* Upload Widget */}
          <div className={`csf-upload-widget ${isParsing ? 'parsing' : ''} ${resumeFile && !isParsing ? 'has-file' : ''}`}>
            <input
              type="file"
              className="csf-file-input"
              accept=".pdf,application/pdf"
              onChange={handleFileChange}
              disabled={isParsing || isLoading}
              required
            />
            <div className="csf-upload-icon-wrap">
              <FileText size={20} color={isParsing ? '#3b82f6' : '#10b981'} />
            </div>
            <h5>
              {isParsing ? 'Analyzing…'
                : resumeFile ? 'Click to replace'
                : 'Upload PDF Resume'}
            </h5>
            <p>
              Only <strong>PDF</strong> accepted.
              AI extracts info automatically.
            </p>

            {resumeFile && !isParsing && (
              <div className="csf-file-chosen">
                <FileText size={13} color="#10b981" />
                <span className="csf-file-chosen-name">{resumeFile.name}</span>
                <span className="csf-file-chosen-size">{fmt(resumeFile.size)}</span>
              </div>
            )}

            {isParsing && (
              <div className="csf-parsing-bar">
                <Loader2 size={14} className="csf-spin" />
                <span>Extracting information…</span>
              </div>
            )}
          </div>

          {/* Checklist */}
          <div className="csf-steps">
            {steps.map((s, i) => (
              <div key={i} className={`csf-step ${s.done ? 'done' : ''}`}>
                <div className="csf-step-num">
                  {s.done ? <CheckCircle size={12} /> : i + 1}
                </div>
                <span className="csf-step-text">{s.label}</span>
              </div>
            ))}
          </div>

          {/* Submit */}
          <button
            className="csf-submit-btn"
            form="csf-main-form"
            type="submit"
            disabled={isLoading || !resumeFile || isParsing}
          >
            {isLoading
              ? <><Loader2 size={16} className="csf-spin" /> Submitting…</>
              : <><Send size={15} /> Submit Candidate</>
            }
          </button>

          <p className="csf-submit-note">
            Submission will be reviewed by the hiring team.
          </p>

        </div>
        {/* end right */}

      </div>
      {/* end body */}

    </div>
  );
}